import {
  assertCrmTransitionAllowed,
  getCrmPhase,
  getCrmPhaseIndex,
} from './crmStateMachine';

function safeUuid(value) {
  const text = String(value || '').trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(text)
    ? text
    : null;
}

export function buildCrmTransitionPlan(fromStage, targetStage, options = {}) {
  const {
    allowOverride = false,
    allowBackwards = false,
    allowCompound = false,
  } = options;

  const fromIndex = getCrmPhaseIndex(fromStage, null);
  const toIndex = getCrmPhaseIndex(targetStage, null);

  if (fromIndex === null || toIndex === null) {
    return { ok: false, error: 'Fase CRM invalida.', steps: [] };
  }

  if (fromIndex === toIndex) {
    return {
      ok: true,
      noop: true,
      from: getCrmPhase(fromIndex),
      to: getCrmPhase(toIndex),
      steps: [],
    };
  }

  if (allowOverride || (allowBackwards && toIndex < fromIndex)) {
    return {
      ok: true,
      from: getCrmPhase(fromIndex),
      to: getCrmPhase(toIndex),
      steps: [{ from: getCrmPhase(fromIndex), to: getCrmPhase(toIndex) }],
    };
  }

  if (allowCompound && toIndex > fromIndex) {
    const steps = [];
    for (let index = fromIndex + 1; index <= toIndex; index += 1) {
      const transition = assertCrmTransitionAllowed(index - 1, index);
      if (!transition.ok) {
        return { ok: false, error: transition.error, steps: [] };
      }
      steps.push({ from: transition.from, to: transition.to });
    }
    return {
      ok: true,
      from: getCrmPhase(fromIndex),
      to: getCrmPhase(toIndex),
      steps,
      compound: steps.length > 1,
    };
  }

  const transition = assertCrmTransitionAllowed(fromIndex, toIndex, {
    allowOverride,
    allowBackwards,
  });

  if (!transition.ok) {
    return { ok: false, error: transition.error, steps: [] };
  }

  return {
    ok: true,
    from: transition.from,
    to: transition.to,
    steps: [{ from: transition.from, to: transition.to }],
  };
}

export async function callAdvanceClientPhaseRpc(supabase, {
  clienteId,
  targetStage,
  source = 'frontend',
  actorId = null,
  metadata = {},
  relatedEntityType = null,
  relatedEntityId = null,
  allowOverride = false,
  allowBackwards = false,
  allowCompound = false,
} = {}) {
  if (!supabase?.rpc) {
    return { ok: false, error: 'Supabase no esta disponible para avanzar fase CRM.' };
  }

  const targetIndex = getCrmPhaseIndex(targetStage, null);
  if (targetIndex === null) {
    return { ok: false, error: 'Fase CRM destino invalida.' };
  }

  const { data, error } = await supabase.rpc('advance_client_phase', {
    p_cliente_id: clienteId,
    p_target_stage: targetIndex,
    p_source: source,
    p_actor_id: safeUuid(actorId),
    p_metadata: metadata && typeof metadata === 'object' ? metadata : {},
    p_related_entity_type: relatedEntityType || null,
    p_related_entity_id: safeUuid(relatedEntityId),
    p_allow_override: Boolean(allowOverride),
    p_allow_backwards: Boolean(allowBackwards),
    p_allow_compound: Boolean(allowCompound),
  });

  if (error) {
    return { ok: false, error: error.message || 'No se pudo avanzar la fase CRM.', raw: error };
  }

  if (data?.ok === false) {
    return { ok: false, error: data.error || 'No se pudo avanzar la fase CRM.', data };
  }

  return data?.ok === true ? data : { ok: true, data };
}
